'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

interface Props {
  onClose: () => void;
}

type TransferState = 'select' | 'transferring' | 'success';

const connectedAgencies = [
  { code: 'JPJ', name: 'Jabatan Pengangkutan Jalan', route: 'CX Controller · MyGovNet', available: true },
  { code: 'JPN', name: 'Jabatan Pendaftaran Negara', route: 'CX Controller · MyGovNet', available: true },
  { code: 'KESUMA', name: 'Kementerian Sumber Manusia', route: 'CX Controller · MyGovNet', available: true },
  { code: 'JIM', name: 'Jabatan Imigresen Malaysia', route: 'CX Controller · MyGovNet', available: true },
  { code: 'KKM', name: 'Kementerian Kesihatan Malaysia', route: 'CX Controller · MyGovNet', available: false },
  { code: 'LHDNM', name: 'Lembaga Hasil Dalam Negeri Malaysia', route: 'CX Controller · MyGovNet', available: true },
];

export default function TransferPanel({ onClose }: Props) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<typeof connectedAgencies[0] | null>(null);
  const [externalNumber, setExternalNumber] = useState('');
  const [transferState, setTransferState] = useState<TransferState>('select');
  const [transferRef] = useState(`TRF-${Date.now().toString().slice(-8)}`);

  const filtered = connectedAgencies.filter(a =>
    !search || a.code.toLowerCase().includes(search.toLowerCase()) || a.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleTransfer = (agency?: typeof connectedAgencies[0]) => {
    const target = agency || selected;
    if (!target && !externalNumber) {
      toast.error('Please select an agency or enter a number');
      return;
    }
    setTransferState('transferring');
    setTimeout(() => setTransferState('success'), 3000);
  };

  if (transferState === 'transferring') {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-full space-y-6">
        <div className="text-center">
          <div className="text-[16px] font-700 text-foreground mb-1">Transferring Call</div>
          <div className="text-[13px] text-muted-foreground">Ahmed Firdaus</div>
        </div>
        {/* Transfer route visualization */}
        <div className="w-full max-w-xs space-y-2">
          {[
            { label: 'MyGCC', icon: 'BuildingOffice2Icon', active: true },
            { label: 'CX Controller', icon: 'ServerIcon', active: true },
            { label: 'MyGovNet', icon: 'GlobeAltIcon', active: true },
            { label: selected?.name || 'Destination', icon: 'BuildingOfficeIcon', active: false },
          ].map((step, i) => (
            <div key={i} className="flex flex-col items-center">
              <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border w-full ${step.active ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-muted border-border text-muted-foreground'}`}>
                <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={14} />
                <span className="text-[12px] font-600">{step.label}</span>
                {step.active && (
                  <div className="ml-auto flex gap-0.5">
                    {[0,1,2].map(j => <span key={j} className="w-1.5 h-1.5 bg-primary rounded-full ai-listening-dot" style={{ animationDelay: `${j * 0.16}s` }} />)}
                  </div>
                )}
              </div>
              {i < 3 && <div className="w-px h-4 bg-border" />}
            </div>
          ))}
        </div>
        <div className="text-[12px] text-muted-foreground">Establishing connection...</div>
      </div>
    );
  }

  if (transferState === 'success') {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-full space-y-4">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
          <Icon name="CheckCircleIcon" size={32} className="text-green-600" />
        </div>
        <div className="text-center">
          <div className="text-[16px] font-700 text-green-700 mb-1">Transfer Successful</div>
          <div className="text-[13px] text-muted-foreground mb-3">Ahmed Firdaus has been transferred</div>
        </div>
        <div className="w-full max-w-xs bg-muted/50 rounded-xl p-4 space-y-2 text-[12px]">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Transferred To</span>
            <span className="font-600 text-foreground">{selected?.name || externalNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Transfer Route</span>
            <span className="font-600 text-foreground">{selected ? 'CX Controller · MyGovNet' : 'TM Voice Network'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Transfer Ref.</span>
            <span className="font-600 text-foreground font-mono">{transferRef}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Context</span>
            <span className="font-600 text-green-600">Prepared ✓</span>
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground text-center bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 w-full max-w-xs">
          Customer context, interaction history, and AI summary have been prepared for the receiving agent.
        </div>
        <button onClick={onClose} className="btn-primary px-6 py-2 text-[13px]">
          Back to Workspace
        </button>
      </div>
    );
  }

  return (
    <div className="p-5 max-w-lg mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[16px] font-700 text-foreground">Transfer Call</h2>
          <div className="text-[12px] text-muted-foreground">Ahmed Firdaus · +6012 *** 4581</div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
          <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
        </button>
      </div>

      {/* Context prepared banner */}
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
        <Icon name="SparklesIcon" size={13} className="text-primary flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-blue-800">
          <span className="font-700">Context Prepared for Transfer</span> — Customer identity, interaction history, ticket reference, detected intent, and AI summary will be available to the receiving agent.
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search agency or number..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-[13px] border border-border rounded-xl bg-background focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      {/* MyGCC Connected Agencies */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground">MyGCC Connected Agencies</div>
          <div className="flex items-center gap-1 text-[10px] text-primary bg-primary/10 px-2 py-0.5 rounded-full">
            <Icon name="SignalIcon" size={10} />
            28 agencies
          </div>
        </div>
        <div className="space-y-1.5">
          {filtered.map(agency => (
            <div
              key={agency.code}
              onClick={() => setSelected(agency)}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                selected?.code === agency.code
                  ? 'border-primary bg-secondary/50' :'border-border hover:border-primary/40 hover:bg-muted/30'
              }`}
            >
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-700 text-primary">{agency.code.slice(0, 3)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-600 text-foreground">{agency.name}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Icon name="SignalIcon" size={10} className="text-primary" />
                  <span className="text-[10px] text-primary font-500">{agency.route}</span>
                </div>
              </div>
              <div className="flex-shrink-0">
                {agency.available ? (
                  <span className="text-[10px] bg-green-50 text-green-600 border border-green-200 px-1.5 py-0.5 rounded font-600">Available</span>
                ) : (
                  <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded font-600">Busy</span>
                )}
              </div>
              {selected?.code === agency.code && (
                <Icon name="CheckCircleIcon" size={16} className="text-primary flex-shrink-0" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* External destination */}
      <div className="mb-4">
        <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-2">External Destination</div>
        <div className="border border-border rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="PhoneIcon" size={13} className="text-muted-foreground" />
            <span className="text-[12px] text-muted-foreground">Enter Telephone Number</span>
          </div>
          <input
            type="text"
            placeholder="+60 3 XXXX XXXX"
            value={externalNumber}
            onChange={e => { setExternalNumber(e.target.value); setSelected(null); }}
            className="w-full px-3 py-2 text-[13px] border border-border rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-primary mb-2"
          />
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <Icon name="SignalIcon" size={10} />
            <span>TM Voice Network · External Telephony</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => { toast.info('Consult mode — connecting supervisor'); }}
          className="flex-1 btn-secondary py-2.5 text-[13px] flex items-center justify-center gap-2"
        >
          <Icon name="UserPlusIcon" size={14} />
          Consult First
        </button>
        <button
          onClick={() => handleTransfer()}
          disabled={!selected && !externalNumber}
          className="flex-1 btn-primary py-2.5 text-[13px] flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Icon name="ArrowRightOnRectangleIcon" size={14} />
          Transfer Now
        </button>
      </div>
    </div>
  );
}
